import { useCallback, useState } from 'react'

const STORAGE_KEY_PREFIX = 'happytask:quick-notes:collapsed:'

/**
 * Persists collapsed state of Quick Notes virtual sections per lifespace.
 *
 * Virtual sections have no DB record, so collapse state lives in localStorage.
 * Stored as a JSON array of collapsed section IDs, scoped by lifespace.
 */
export function useQuickNotesCollapsedState(lifeSpace: 'personal' | 'work' | 'home') {
  const storageKey = `${STORAGE_KEY_PREFIX}${lifeSpace}`

  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return new Set()
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? new Set(parsed.filter((v): v is string => typeof v === 'string')) : new Set()
    } catch {
      return new Set()
    }
  })

  const isCollapsed = useCallback(
    (sectionId: string) => collapsedIds.has(sectionId),
    [collapsedIds],
  )

  const toggleCollapsed = useCallback((sectionId: string) => {
    setCollapsedIds(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) next.delete(sectionId)
      else next.add(sectionId)
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]))
      } catch {
        // ignore quota/serialization errors
      }
      return next
    })
  }, [storageKey])

  return { isCollapsed, toggleCollapsed }
}
